import { Dispatch, SetStateAction, ReactNode, useState, useEffect } from "react";
import { useTranslation } from "@/i18n/client";
import Dropzone from "@/components/Dropzone";
import { FileRejection, FileWithPath } from "@mantine/dropzone";
import { MIME_TYPES } from "@mantine/dropzone";
import FileBox from "@/components/FileBox";
import { FileError } from "@/screens/common/Attachments/FileError";
import { Attachment } from "@/types";
import { notifications } from "@mantine/notifications";
import { useMutation } from "@tanstack/react-query";
import { deleteAttachment, deleteAttachmentRequest } from "@/requests/attachment/delete";
import { v4 as uuidv4 } from "uuid";
import { ErrorCode } from "react-dropzone-esm";
import helperClasses from "./index.module.css";
import Alert from "@/components/Alert";
import { ContractState } from "@/types/contract";
import { AttachmentOptions, ErrorCodeLocal, unsuportedJpegFileExtensions, uploadState } from "@/constants/attachment";
import { useQueryClient } from "@tanstack/react-query";
import { ValidationError } from "@/components/ValidationError";
import { InputTooltip } from "@/components/InputTooltip";
import { useIsContractRequest } from "@/hooks/useIsContractRequest";
import { sessionStorageKeys, ReactQueryKeys } from "@/constants/common";
import { creteUploadFormData } from "@/utils/file";

// 20 MB
const MAX_SIZE_BYTES = 20 * 1024 ** 2;

const MAX_FILES = 10;

interface AttachmentsProps extends AttachmentOptions {
  contractState: ContractState;
  helperText: ReactNode;
  fileTypeCode: string;
  onSetUploaded?: (state: uploadState) => void;
  setTooltipVisible?: Dispatch<SetStateAction<boolean>>;
  tooltipVisible?: boolean;
  validationError?: string;
}

export const Attachments = ({
  contractState,
  helperText,
  fileTypeCode,
  onSetUploaded,
  tooltipVisible,
  setTooltipVisible,
  validationError,
  biometric,
  fileType,
  groupId,
  humanName,
  internal,
  paperRequest,
}: AttachmentsProps) => {
  const { contract, uuid, sessionKey } = contractState;
  const { t } = useTranslation("common");
  const queryClient = useQueryClient();

  const [filesFromBE, setFilesFromBE] = useState<Attachment[]>(
    contract.attachments
      .filter(attachment => attachment.fileTypeCode === fileTypeCode && !attachment.forRepair)
      .filter(attachment => (paperRequest ? attachment.paperRequest : true))
  );

  const [filesWithProgress, setFilesWithProgress] = useState<Array<{ file: FileWithPath; progress: number; uuid?: string }>>([]);
  const isRequest = useIsContractRequest();

  useEffect(() => {
    const uploadInProgress = filesWithProgress.some(f => f.progress < 100);
    if (uploadInProgress && onSetUploaded) {
      onSetUploaded("uploadInProgress");
    } else if (filesWithProgress.length > 0 && onSetUploaded) {
      onSetUploaded("uploaded");
    }
  }, [filesWithProgress, onSetUploaded]);

  const [tooManyFilesDroppedAtOnce, setTooManyFilesDroppedAtOnce] = useState(false);

  const setNoFilesInformation = (arrWithDeletedFile: Array<unknown>, arrWithoutDeletedFile: Array<unknown>) => {
    if (onSetUploaded && arrWithDeletedFile.length === 1 && arrWithoutDeletedFile.length === 0) {
      onSetUploaded("noFiles");
    }
  };

  const handleInformationAboutDeletedFile = (attachmentId: string) => {
    if (filesFromBE.find(file => file.uid === attachmentId)) {
      setNoFilesInformation(
        filesFromBE,
        filesWithProgress.filter(file => file.progress === 100)
      );
      return;
    }
    setNoFilesInformation(filesWithProgress, filesFromBE);
  };

  const mutation = useMutation({
    mutationFn: isRequest ? deleteAttachmentRequest : deleteAttachment,
    onSuccess: attachmentId => {
      handleInformationAboutDeletedFile(attachmentId);

      setFilesFromBE(prevState => prevState.filter(file => file.uid !== attachmentId));
      setFilesWithProgress(prevState => prevState.filter(file => file.uuid !== attachmentId));

      void queryClient.invalidateQueries({ queryKey: ReactQueryKeys.getContract() });
    },
    onError: () => {
      notifications.show({
        title: t("deleteAttachmentNotSuccessful"),
        message: "",
        color: "red",
      });
    },
  });

  const [rejectedFiles, setRejectedFiles] = useState<FileRejection[]>([]);

  const handleErrorUpload = (file: FileWithPath) => {
    setFilesWithProgress(prevState => {
      const nextState = prevState.filter(prevFile => prevFile.file.path !== file.path);
      // A failed upload can be the last in-flight one, so refresh the already
      // uploaded attachments here too instead of losing the pending invalidation.
      if (nextState.length > 0 && nextState.every(prevFile => prevFile.progress === 100)) {
        void queryClient.invalidateQueries({ queryKey: ReactQueryKeys.getContract() });
      }
      return nextState;
    });
    setRejectedFiles(prevState => [
      {
        errors: [
          {
            message: "BE could not process",
            code: "be-error",
          },
        ],
        file,
      },
      ...prevState,
    ]);
  };

  const handleSetProgress = (file: FileWithPath, progress: number) => {
    setFilesWithProgress(prevState =>
      prevState.map(prevFile => (prevFile.file.path === file.path ? { ...prevFile, progress } : prevFile))
    );
  };

  const handleSuccessUpload = (file: FileWithPath, uuid: string) => {
    setFilesWithProgress(prevState => {
      const nextState = prevState.map(prevFile =>
        prevFile.file.path === file.path ? { ...prevFile, progress: 100, uuid } : prevFile
      );
      // Invalidate only once all in-flight uploads finished, otherwise the contract
      // refetch remounts this component and wipes the still-uploading loaders.
      if (nextState.every(prevFile => prevFile.progress === 100)) {
        void queryClient.invalidateQueries({ queryKey: ReactQueryKeys.getContract() });
      }
      return nextState;
    });
  };

  // eslint-disable-next-line sonarjs/cognitive-complexity
  const handleDrop = (files: FileWithPath[]) => {
    const newFilesWithProgress = files.map(file => ({ file, progress: 0 }));
    setFilesWithProgress(prevFiles => [...newFilesWithProgress, ...prevFiles]);

    files.forEach(file => {
      const reader = new FileReader();

      reader.onabort = () => {
        handleErrorUpload(file);
      };

      reader.onerror = () => {
        handleErrorUpload(file);
      };

      reader.onload = () => {
        const binaryStr = reader.result;
        if (binaryStr === null) {
          return;
        }
        const fileUuid = uuidv4();
        const blob = new Blob([binaryStr], { type: file.type });
        const formData = creteUploadFormData(blob, fileUuid, contract.customer.id, file.name, fileTypeCode, isRequest, {
          biometric,
          fileType,
          groupId,
          humanName,
          internal,
          paperRequest,
        });

        const xhr = new XMLHttpRequest();
        xhr.open(
          "POST",
          isRequest
            ? `${process.env.NEXT_PUBLIC_API_URL}/web_api/request/${uuid}/attachment`
            : `${process.env.NEXT_PUBLIC_API_URL}/web_api/contract/${uuid}/attachment`,
          true
        );

        const apiToken = sessionKey ?? sessionStorage.getItem(sessionStorageKeys.sessionKey);
        if (apiToken) {
          xhr.setRequestHeader("X-Api-Token", apiToken);
        }

        xhr.upload.onprogress = event => {
          const percentages = +((event.loaded / event.total) * 100).toFixed(2);
          handleSetProgress(file, percentages === 100 ? 99 : percentages);
        };

        xhr.onreadystatechange = () => {
          if (xhr.readyState !== 4) {
            return;
          }
          if (xhr.status !== 201 || typeof xhr.response !== "string") {
            handleErrorUpload(file);
            return;
          }
          handleSuccessUpload(file, fileUuid);
        };

        xhr.send(formData);
      };
      reader.readAsArrayBuffer(file);
    });
  };

  const handleValidator = <T extends FileWithPath>(file: T) => {
    const fileName = file.name ? file.name.toLowerCase() : "";
    if (unsuportedJpegFileExtensions.some(ext => fileName.endsWith(ext))) {
      return {
        message: "unsuported JPEG file extension",
        code: ErrorCodeLocal.unsuportedJpegExtension,
      };
    }
    if (filesWithProgress.some(filesWithProgress => filesWithProgress.file.path === file.path)) {
      return {
        message: "file is already loading",
        code: ErrorCodeLocal.alreadyLoading,
      };
    }
    if (filesWithProgress.length + filesFromBE.length + 1 > MAX_FILES) {
      return {
        message: "max files",
        code: ErrorCode.TooManyFiles,
      };
    }
    return null;
  };

  const handleDeleteAttachment = (attachmentId: string | null) => {
    if (!attachmentId) {
      return;
    }
    mutation.mutate({ contractUuid: uuid, attachmentId });
  };

  const isMaxFiles = (files: Array<unknown>) => {
    if (filesWithProgress.length + filesFromBE.length + files.length > MAX_FILES) {
      setTooManyFilesDroppedAtOnce(true);
      return true;
    }
    if (tooManyFilesDroppedAtOnce) {
      setTooManyFilesDroppedAtOnce(false);
    }
    return false;
  };

  return (
    <>
      <div>
        <InputTooltip
          wrapperClasses={helperClasses.dropzoneWrapper}
          showTooltip={!!helperText}
          tooltipProps={{
            label: helperText,
            opened: tooltipVisible,
            setOpened: setTooltipVisible,
          }}>
          <Dropzone
            validator={handleValidator}
            maxSize={MAX_SIZE_BYTES}
            maxFiles={MAX_FILES}
            accept={[MIME_TYPES.pdf, MIME_TYPES.jpeg, MIME_TYPES.png]}
            hasError={!!validationError}
            label={
              <>
                {t("dropFile")}
                <br />
                {t("formats")}
              </>
            }
            buttonLabel={t("chooseFile")}
            onDrop={files => {
              if (isMaxFiles(files)) {
                return;
              }
              setRejectedFiles([]);
              handleDrop(files);
            }}
            onReject={files => {
              if (isMaxFiles(files)) {
                return;
              }
              setRejectedFiles(files);
            }}
          />
        </InputTooltip>
        <ValidationError>{validationError}</ValidationError>
      </div>
      {tooManyFilesDroppedAtOnce && (
        <Alert variant="error" onClose={() => setTooManyFilesDroppedAtOnce(false)}>
          {t("fileError.tooManyFilesAtOnce.part1")}
          {MAX_FILES}
          {t("fileError.tooManyFilesAtOnce.part2")}
        </Alert>
      )}
      {rejectedFiles.map(rejectedFile => (
        <FileError
          key={"path" in rejectedFile.file ? rejectedFile.file.path : rejectedFile.file.name}
          name={rejectedFile.file.name}
          code={rejectedFile.errors[0]?.code ?? ""}
          onClose={() =>
            setRejectedFiles(prevRejectedFiles =>
              prevRejectedFiles.filter(fileWithError =>
                "path" in fileWithError.file && "path" in rejectedFile.file
                  ? fileWithError.file.path !== rejectedFile.file.path
                  : fileWithError.file.name !== rejectedFile.file.name
              )
            )
          }
        />
      ))}
      {filesWithProgress.map(fileWithProgress => {
        const { file, progress, uuid } = fileWithProgress;
        const { path, name, size } = file;
        return (
          <FileBox
            key={path}
            label={name}
            removeLabel={t("removeFile", { name })}
            progressValue={progress}
            fileSize={size}
            showCancel={uuid !== undefined}
            onCancel={() => {
              if (uuid !== undefined) {
                handleDeleteAttachment(uuid);
              }
            }}
          />
        );
      })}
      {filesFromBE.map(file => {
        if (!file.uid) {
          return;
        }
        return (
          <FileBox
            key={file.uid}
            label={file.fileName ?? file.humanName}
            removeLabel={t("removeFile", { name })}
            progressValue={100}
            fileSize={file.size}
            showCancel
            onCancel={() => handleDeleteAttachment(file.uid)}
          />
        );
      })}
    </>
  );
};
